import { Button, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfringementStream } from "../hooks/useInfringementStream";
import { useTripTrackingActivity } from "../hooks/useTripTrackingActivity";
import { useTripLiveTracking } from "../hooks/useTripLiveTracking";
import { mergeTrackingWithLiveSamples } from "../services/firestoreTracking.service";
import { LiveVehiclePanel } from "./live_vehicle_panel.component";
import { OfflinePanel } from "./offline_panel.component";
import { SpeedingPanel } from "./speeding_panel.component";

export const TrackingComplianceSection = ({ tripId, expanded, trip, preInspection, tripStatusLabel }) => {
  const navigate = useNavigate();
  const [clockMs, setClockMs] = useState(Date.now());
  const tracking = useTripLiveTracking(tripId, Boolean(expanded && tripId));
  const trackingActivity = useTripTrackingActivity(tripId, Boolean(expanded && tripId));
  const infringementStream = useInfringementStream({
    tripId,
    limitCount: 40,
    enabled: Boolean(expanded && tripId),
  });

  useEffect(() => {
    if (!expanded || !tripId) return undefined;
    const intervalId = setInterval(() => setClockMs(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [expanded, tripId]);

  const liveTracking = useMemo(
    () => mergeTrackingWithLiveSamples(tracking.data, trackingActivity.trackingSamples, clockMs),
    [tracking.data, trackingActivity.trackingSamples, clockMs]
  );
  const speeding = infringementStream.records.filter((record) => record?.type === "speeding");
  const offline = trackingActivity.offlineInfringements.length
    ? trackingActivity.offlineInfringements
    : infringementStream.records.filter((record) => record?.type === "offline");

  return <div className="mt-6 rounded-[28px] border border-[#ece5dd] bg-[linear-gradient(180deg,#fffdf9_0%,#ffffff_100%)] px-8 py-7 shadow-[0_18px_55px_rgba(15,23,42,0.05)] flex flex-col gap-6">
    <div className="flex flex-wrap items-start justify-between gap-5">
      <div className="flex flex-col gap-2">
        <span className="text-[1.15rem] font-semibold text-[#161311]">Tracking &amp; Compliance</span>
        <span className="text-sm text-[#6e6258]">Live Firestore telemetry plus denormalized speeding and offline infringements.</span>
      </div>
      <Button type="primary" onClick={() => navigate(`/track?tripId=${tripId}`)}>Track Vehicle</Button>
    </div>

    {(tracking.loading || trackingActivity.loading || infringementStream.loading) ? <div className="self-center py-10"><Spin /></div> : <>
      <LiveVehiclePanel
        trip={trip}
        preInspection={preInspection}
        tracking={liveTracking}
        tripStatusLabel={tripStatusLabel}
        trackingPointCount={trackingActivity.trackingSamples.length || trackingActivity.trackingEvents.length}
        offlineCount={offline.length}
        speedingCount={speeding.length}
        compact
      />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SpeedingPanel records={speeding} />
        <OfflinePanel records={offline} />
      </div>
    </>}
  </div>;
};
